import {useQuery} from "@tanstack/react-query";
import {useCallback, useRef} from "react";
import {GetTransactionsPaginatedResponse, Token, TransactionsApi} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {useStatsigClient} from "@statsig/react-bindings";

/**
 * Canonical, single source of truth for the user's Fuel balance and its USD value.
 *
 * WHY THIS EXISTS — financial integrity:
 * The Fuel balance is money. It must render IDENTICALLY on every surface at any
 * given moment. Previously the balance was read through two different endpoints
 * (`/transactions/:id` via useGetTransactions and `/transactions-paginated/:id`
 * via useGetTransactionsPaginated) with independent React Query caches, and some
 * callers passed inconsistent params (so even same-endpoint callers landed in
 * different cache entries). Each cache sampled the live FuelX price at its own
 * fetch time, so the exact same balance rendered as different USD amounts across
 * the app (e.g. $37,051 in the nav vs $38,195 at checkout), and one legacy peg
 * even fabricated the Fuel QUANTITY from USD × 4235.
 *
 * This hook collapses all of that to ONE stable queryKey → ONE cache entry that
 * every display shares and refetches together. Read `balance` for the quantity
 * (NEVER derive it) and `balanceInUsd` for the dollar value (server-computed as
 * balance × the 30s-cached FuelX price — never recompute it against a separately
 * fetched rate).
 *
 * The paginated endpoint computes `balance`/`balanceInUsd`/`fuelPricePerUsd` over
 * the user's full ledger regardless of limit/offset, so we request the smallest
 * page (limit 1) — we only consume the balance fields, not the transaction list.
 * Transaction-history pages keep useGetTransactionsPaginated for the actual list.
 */

/** Shared query-key root — invalidate this to refresh the balance everywhere at once. */
export const FUEL_BALANCE_QUERY_KEY = "fuel-balance";

/**
 * Canonical USD formatter for the Fuel balance. EVERY surface that shows the
 * balance in dollars MUST use this so the same balance can never render as two
 * different figures (e.g. $38,163 vs $38,164 from one place flooring and another
 * rounding).
 *
 * Whole-dollar mode FLOORS: a spendable balance must never display LARGER than it
 * actually is. `cents` mode shows the exact 2-decimal value (balanceInUsd is
 * already rounded to cents server-side, so there is no further rounding choice).
 */
export function formatFuelUsd(balanceInUsd?: number, opts?: { cents?: boolean; symbol?: boolean }): string {
    const value = balanceInUsd ?? 0;
    // Symbol on by default; pass symbol:false for callers that prepend their own "$".
    const prefix = opts?.symbol === false ? "" : "$";
    if (opts?.cents) {
        return `${prefix}${new Intl.NumberFormat("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(value)}`;
    }
    // Floor first, then format with no fraction digits — guarantees truncation, never rounding-up.
    return `${prefix}${new Intl.NumberFormat("en-US", {maximumFractionDigits: 0}).format(Math.floor(value))}`;
}

/** Canonical Fuel-quantity formatter — whole Fuel with thousands separators. Never derive qty from USD. */
export function formatFuelQty(balance?: number): string {
    return new Intl.NumberFormat("en-US").format(Math.floor(balance ?? 0));
}

async function fetchFuelBalance(fullToken: Token, userId: string): Promise<GetTransactionsPaginatedResponse> {
    /*
     * No null guard needed — the query's `enabled` flag below already prevents
     * this from running without a token/userId. Returning the response directly
     * keeps `data` typed exactly like useGetTransactionsPaginated (a clean
     * drop-in), so migrated call sites and typed props need no changes.
     */
    return await new TransactionsApi(getConfig(fullToken)).getTransactionsPaginatedId({
        id: userId,
        limit: 1,
        offset: 0,
    });
}

export function useGetFuelBalance(fullToken?: Token, userId?: string, disabled?: boolean) {
    const {client: statsigClient} = useStatsigClient();
    const fuelStatus = !statsigClient.checkGate(import.meta.env.VITE_TOKEN_STATSIG_STATUS);

    const query = useQuery({
        /*
         * STABLE key — no pagination params. This is the whole point: one cache
         * entry for "this user's Fuel balance", shared by every surface.
         */
        queryKey: [FUEL_BALANCE_QUERY_KEY, fullToken?.id ?? "unauthenticated", userId ?? "no-user"],
        queryFn: () => fetchFuelBalance(fullToken as Token, userId as string),
        enabled: Boolean(fullToken && userId && fuelStatus) && !disabled,
        /*
         * 30s matches the backend FuelX price cache — refetching faster can't
         * surface a newer price. All consumers share this window so they move in
         * lockstep; refetchInterval keeps the displayed price current without a
         * manual reload, and refetchOnWindowFocus stays off so a tab switch never
         * splits one surface's snapshot from another's.
         */
        staleTime: 30 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchInterval: 30 * 1000,
        refetchOnWindowFocus: false,
    });

    /*
     * React Query's refetch() BYPASSES `enabled`, so an imperative refetch() from
     * any consumer (e.g. after a gift-card order, before useGetUserProfile resolves)
     * would call getTransactionsPaginatedId with id=undefined — the generated client
     * throws RequiredError, surfaced to the user as an "Unable to Complete" modal.
     * Guard refetch at the source so EVERY consumer is inherently safe and no call
     * site needs its own wrapper. Ref keeps the no-op return current without
     * destabilising the callback identity.
     */
    const latest = useRef(query);
    latest.current = query;
    const safeRefetch = useCallback<typeof query.refetch>(
        (options) => fullToken && userId ? latest.current.refetch(options) : Promise.resolve(latest.current),
        [fullToken, userId],
    );

    return {...query, refetch: safeRefetch};
}
